import { forwardRef, useEffect } from 'react';
import { HexColor } from '@ardoq/api-types';
import { ColorSwatch } from './ColorSwatch';
import { FULL_TRANSPARENT, Field, FieldProps } from '@ardoq/forms';
import { AddIcon, Icon, IconName, IconSize } from '@ardoq/icons';
import { GhostButton } from '@ardoq/button';
import { usePrevious } from '@ardoq/hooks';
import { ComponentWithDropdown } from '@ardoq/dropdown-menu';
import { FlexBox } from '@ardoq/layout';

export const MIN_AMOUNT_OF_COLORS_IN_THEME = 8;

type ColorThemeBuilderProps = {
  themeColors: Array<HexColor | null>;
  openColorPicker: (colorThemeIndex: number, color?: HexColor) => void;
  selectedColorThemeIndex: null | number;
  updateThemeColors: (themeColors: Array<HexColor | null>) => void;
  addColorToTheme: VoidFunction;
  duplicateColorsErrorMessage?: string;
  editColorLabel?: string;
  deleteColorLabel?: string;
} & Pick<
  FieldProps,
  | 'indicator'
  | 'errorMessage'
  | 'warningMessage'
  | 'dataTestId'
  | 'helperText'
  | 'popoverContent'
>;

export const ColorThemeBuilder = forwardRef<
  HTMLDivElement,
  ColorThemeBuilderProps
>(
  (
    {
      themeColors,
      openColorPicker,
      selectedColorThemeIndex,
      addColorToTheme,
      updateThemeColors,
      duplicateColorsErrorMessage = 'Make sure that the selected colors are distinct',
      editColorLabel = 'Edit',
      deleteColorLabel = 'Delete',
      ...fieldProps
    },
    ref
  ) => {
    const previousThemeColors = usePrevious(themeColors);
    const isColorSwatchAdded =
      previousThemeColors?.length &&
      previousThemeColors?.length < themeColors.length;

    useEffect(() => {
      if (isColorSwatchAdded) {
        // This is so that the color picker opens automatically when a swatch is added
        openColorPicker(themeColors.length - 1);
      }
    }, [isColorSwatchAdded, themeColors, openColorPicker]);

    const deleteColor = (colorIndex: number) =>
      updateThemeColors(
        themeColors.length <= MIN_AMOUNT_OF_COLORS_IN_THEME
          ? themeColors.map((color, i) => (i === colorIndex ? null : color))
          : themeColors.filter((_, i) => colorIndex !== i)
      );

    const nonNullThemeColors = themeColors.filter(Boolean);
    const hasDuplicatedColors =
      new Set(nonNullThemeColors).size !== nonNullThemeColors.length;

    return (
      <Field
        {...fieldProps}
        errorMessage={
          hasDuplicatedColors ? duplicateColorsErrorMessage : undefined
        }
        ref={ref}
      >
        <FlexBox
          borderColor={hasDuplicatedColors ? 'borderError' : 'borderAction'}
          borderWidth={1}
          borderRadius="r4"
          style={{ width: 'fit-content' }}
        >
          <FlexBox
            style={{ width: '256px' }}
            paddingY="small"
            paddingLeft="small"
            gap="small"
            align="center"
            flexWrap
          >
            {themeColors.map((color, i) =>
              !color ? (
                <ColorSwatch
                  key={`${FULL_TRANSPARENT}-${i}`}
                  isActive={selectedColorThemeIndex === i}
                  color={FULL_TRANSPARENT}
                  onClick={() => openColorPicker(i)}
                  aria-haspopup="dialog"
                  aria-expanded={selectedColorThemeIndex === i}
                >
                  <Icon
                    iconName={FULL_TRANSPARENT as IconName}
                    iconSize={IconSize.LARGE}
                  />
                </ColorSwatch>
              ) : (
                <ComponentWithDropdown
                  key={`${color}-${i}`}
                  Component={forwardRef<
                    HTMLButtonElement,
                    {
                      onMenuOpen: VoidFunction;
                      isMenuOpened: boolean;
                    }
                  >(({ onMenuOpen, isMenuOpened }, ref) => (
                    <ColorSwatch
                      hasError={
                        hasDuplicatedColors &&
                        themeColors.some(
                          (otherColor, j) => otherColor === color && i !== j
                        )
                      }
                      ref={ref}
                      color={color}
                      onClick={onMenuOpen}
                      isActive={isMenuOpened || selectedColorThemeIndex === i}
                      aria-haspopup="menu"
                      aria-expanded={isMenuOpened}
                    />
                  ))}
                  options={[
                    {
                      label: editColorLabel,
                      iconName: IconName.EDIT,
                      onClick: () => openColorPicker(i, color),
                    },
                    {
                      label: deleteColorLabel,
                      iconName: IconName.DELETE,
                      onClick: () => deleteColor(i),
                    },
                  ]}
                />
              )
            )}
          </FlexBox>
          <FlexBox margin="xsmall">
            <GhostButton
              onClick={addColorToTheme}
              isDisabled={
                themeColors.some(color => !color) // Must fill in the available swatches before creating more swatches
              }
            >
              <AddIcon iconSize={IconSize.SMALL} />
            </GhostButton>
          </FlexBox>
        </FlexBox>
      </Field>
    );
  }
);
